import {
  Box,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import bbox from "@turf/bbox";
import React, { useEffect, useRef, useState, useMemo } from "react";
import wkt from "wellknown";
import { useConfig } from "../../contexts/ConfigContext";
import { useOptions } from "../../contexts/OptionsContext";
import {
  GeoJSONFeature,
  GeoJSONGeometry,
  MediaItemDetailed,
  PreviewMapData,
  TaggedItem,
  RelatedEntityItem,
} from "../../types";
import { apiFetch, REACT_APP_API_ROOT } from "../../utilities/api";
import { getCategoryColor } from "../../utilities/dataItemUtils";
import { CMChip } from "../common/CMChip";
import { PreviewMap } from "../common/PreviewMap";
import MediaViewer from "../media/MediaViewer";

interface UploadFile {
  url: string;
  name: string;
  size: number;
  type: string;
}

interface SingleItemPreviewProps {
  item: MediaItemDetailed;
  onUpdate: (updates: Partial<MediaItemDetailed>) => void;
  onPreviewClick: (url: string | undefined) => void;
  mediaUrl?: string;
}

const isTaggedItem = (item: any): item is TaggedItem => {
  return (
    typeof item === "object" && item !== null && "id" in item && "name" in item
  );
};

const SingleItemPreview: React.FC<SingleItemPreviewProps> = ({
  item,
  onUpdate,
  onPreviewClick,
  mediaUrl,
}) => {
  // Initialize with empty arrays from relationships
  const familyAreas = item?.relationships?.geo_entities?.familyarea || [];
  const places = item?.relationships?.geo_entities?.place || [];
  const isMapDataAvailable = familyAreas.length > 0 || places.length > 0;

  const config = useConfig();
  const { options } = useOptions();
  const [mapData, setMapData] = useState<PreviewMapData>({
    areas: [],
    points: [],
  });
  const mapRef = useRef<any>(null);
  const [isMapVisible, setIsMapVisible] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const theme = useTheme();

  // Memoize the preview MediaDisplay to prevent video restarts
  const previewMediaDisplay = useMemo(() => {
    const resolvedUrl = mediaUrl || item?.previewUrl || item?.thumbnail || item?.url || "";
    const filename = item?.original_file?.filename;
    const file = item?.file;
    const mediaType = item?.media_type;
    
    return (
      <MediaViewer
        key={`preview-${item?.id}-${mediaUrl || resolvedUrl}`}
        item={item}
        url={resolvedUrl}
        filename={filename}
        file={!resolvedUrl ? file : undefined}
        mode="preview"
        onClick={() => {
          setIsPreviewOpen(true);
        }}
        width="100%"
        height={mediaType === 'audio' || 
               (item?.url && /\.(mp3|wav|ogg|m4a|flac)$/i.test(item.url.toLowerCase())) 
               ? "80px" : "100%"}
      />
    );
  }, [
    item?.id,
    mediaUrl,
    item?.previewUrl,
    item?.thumbnail,
    item?.url,
    item?.original_file?.filename,
    item?.file?.name,
    item?.media_type
  ]);

  useEffect(() => {
    const fetchData = async () => {
      // Clear existing map data first
      setMapData({ areas: [], points: [] });
      
      if (item && isMapDataAvailable) {
        const geoData = await fetchGeoData();
        setMapData(geoData);
      }
    };

    fetchData();
  }, [
    item?.id,
    item?.relationships?.geo_entities?.familyarea?.length,
    item?.relationships?.geo_entities?.place?.length,
    isMapDataAvailable
  ]);

  useEffect(() => {
    if (
      mapRef.current &&
      (mapData.areas.length > 0 || mapData.points.length > 0)
    ) {
      const features = [...mapData.areas, ...mapData.points];
      const bounds = bbox({
        type: "FeatureCollection",
        features: features,
      });

      mapRef.current.fitBounds(
        [
          [bounds[0], bounds[1]],
          [bounds[2], bounds[3]],
        ],
        {
          padding: 50,
          duration: 1000,
          maxZoom: 10,
        }
      );
    }
  }, [mapData]);

  // Return null early, but after all hooks
  if (!item) {
    return null;
  }

  // Add the getNameForId function
  const getNameForId = (type: string, id: number | string): string => {
    const typeOptions = options?.[type] || [];
    const option = typeOptions.find((opt) => opt.id === id);
    return option?.name || `${type} ${id}`;
  };

  // TODO: Find a way to find the endpoints without hardcoding the table names. For other customers we may use something other that 'family-areas'
  const fetchGeoData = async () => {
    try {
      const countryAreasEndpoint = "api/user/family-areas/";
      const placesEndpoint = "api/user/places/";

      // Add type guard for GeoJSON geometry
      const isValidGeoJSONGeometry = (
        geometry: any
      ): geometry is GeoJSONGeometry => {
        if (!geometry || typeof geometry.type !== "string") return false;

        if (geometry.type === "GeometryCollection") {
          return (
            Array.isArray(geometry.geometries) &&
            geometry.geometries.every((g: any) => isValidGeoJSONGeometry(g))
          );
        }

        return (
          Array.isArray(geometry.coordinates) &&
          [
            "Point",
            "MultiPoint",
            "LineString",
            "MultiLineString",
            "Polygon",
            "MultiPolygon",
          ].includes(geometry.type)
        );
      };

      // Convert data to GeoJSON Feature
      const toGeoJSONFeature = (
        data: any,
        name: string
      ): GeoJSONFeature | null => {
        try {
          // If data is a FeatureCollection, extract the first feature
          if (
            data.type === "FeatureCollection" &&
            Array.isArray(data.features) &&
            data.features.length > 0
          ) {
            const feature = data.features[0];
            if (
              feature.type === "Feature" &&
              isValidGeoJSONGeometry(feature.geometry)
            ) {
              return feature as GeoJSONFeature;
            }
          }

          // If data is already a GeoJSON Feature, validate and return
          if (
            data.type === "Feature" &&
            isValidGeoJSONGeometry(data.geometry)
          ) {
            return data as GeoJSONFeature;
          }

          // If data has a geometry property that's a string (WKT)
          if (data.geometry && typeof data.geometry === "string") {
            const parsedGeometry = wkt.parse(data.geometry);

            if (parsedGeometry && isValidGeoJSONGeometry(parsedGeometry)) {
              const feature: GeoJSONFeature = {
                type: "Feature",
                geometry: parsedGeometry,
                properties: {
                  name: data.name || name,
                  ...data.properties,
                },
              };
              return feature;
            }
          }

          return null;
        } catch (error) {
          return null;
        }
      };

      const countryPromises = familyAreas.map(async (area) => {
        try {
          const data = await apiFetch(
            `${REACT_APP_API_ROOT}${countryAreasEndpoint}${area.id}/`
          );
          return toGeoJSONFeature(data, getNameForId("family-areas", Number(area.id)));
        } catch (error) {
          return null;
        }
      });

      const placePromises = places.map(async (place) => {
        try {
          const data = await apiFetch(
            `${REACT_APP_API_ROOT}${placesEndpoint}${place.id}/`
          );
          return toGeoJSONFeature(data, getNameForId("places", Number(place.id)));
        } catch (error) {
          return null;
        }
      });

      const [countryResults, placeResults] = await Promise.all([
        Promise.all(countryPromises),
        Promise.all(placePromises),
      ]);

      // Filter out null values and ensure we have valid GeoJSON features
      const validAreas = countryResults.filter(
        (result): result is GeoJSONFeature =>
          result !== null && result.type === "Feature" && !!result.geometry
      );
      const validPoints = placeResults.filter(
        (result): result is GeoJSONFeature =>
          result !== null && result.type === "Feature" && !!result.geometry
      );

      return {
        areas: validAreas,
        points: validPoints,
      };
    } catch (error) {
      return { areas: [], points: [] };
    }
  };
  
  // Function to determine if a color is dark (for text contrast)
  const isColorDark = (hexColor: string) => {
    try {
      // Convert hex to RGB
      const r = parseInt(hexColor.slice(1, 3), 16);
      const g = parseInt(hexColor.slice(3, 5), 16);
      const b = parseInt(hexColor.slice(5, 7), 16);
      
      // Calculate brightness based on YIQ formula
      return (r * 299 + g * 587 + b * 114) / 1000 < 128;
    } catch (error) {
      // Default to assuming it's dark
      return true;
    }
  };

  const renderRelationships = () => {
    // If no relationships object exists, show placeholder
    if (!item?.relationships) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No relationships defined
        </Typography>
      );
    }
    
    // Collect all relationships in a flat array
    interface FlatRelationshipItem {
      key: string;
      id: string | number;
      name: string;
      isGeo: boolean;
      category: string;
    }
    
    const allRelationshipItems: FlatRelationshipItem[] = [];
    
    // Process geo_entities
    if (item?.relationships?.geo_entities) {
      Object.entries(item.relationships.geo_entities).forEach(([key, entities]) => {
        if (Array.isArray(entities) && entities.length > 0) {
          entities.forEach(entity => {
            allRelationshipItems.push({
              key,
              id: entity.id,
              name: entity.entity_name || entity.name || `Item ${entity.id}`,
              isGeo: true,
              category: key
            });
          });
        }
      });
    }
    
    // Process related_entities
    if (item?.relationships?.related_entities) {
      Object.entries(item.relationships.related_entities).forEach(([key, entities]) => {
        if (Array.isArray(entities) && entities.length > 0) {
          entities.forEach(entity => {
            allRelationshipItems.push({
              key,
              id: entity.id,
              name: entity.entity_name || entity.name || `Item ${entity.id}`,
              isGeo: false,
              category: key
            });
          });
        }
      });
    }
    
    // Process media items
    if (item?.relationships?.media && Array.isArray(item.relationships.media) && item.relationships.media.length > 0) {
      item.relationships.media.forEach(entity => {
        allRelationshipItems.push({
          key: 'media',
          id: entity.id,
          name: entity.entity_name || entity.name || `Item ${entity.id}`,
          isGeo: false,
          category: 'media'
        });
      });
    }
    
    // If no relationships found after processing, show placeholder
    if (allRelationshipItems.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No relationships found
        </Typography>
      );
    }
    
    // Render all relationships as chips in a single container
    return (
      <Box sx={{ 
        width: '100%',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        justifyContent: 'flex-start'
      }}>
        {allRelationshipItems.map(item => {
          const bgColor = getCategoryColor(item.category);
          const textColor = isColorDark(bgColor) ? '#ffffff' : '#000000';
          
          return (
            <CMChip
              key={`${item.key}-${item.id}`}
              label={item.name}
              size="small"
              sx={{ 
                height: "18px", 
                fontSize: "0.65rem", 
                bgcolor: bgColor,
                color: textColor,
                "& .MuiChip-label": { 
                  px: 0.75,
                  py: 0
                } 
              }}
            />
          );
        })}
      </Box>
    );
  };

  return (
    <>
      <Paper
        elevation={2}
        sx={{
          height: "100%",
          overflow: "hidden",
          bgcolor: "rgba(0, 0, 0, 0.1)",
          padding: 2,
          backdropFilter: "blur(8px)",
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 2, height: "100%", p: 0 }}>
          {/* Left Column - Preview */}
          <Box
            sx={{
              flex: isMapVisible ? "1 1 33%" : "1 1 50%",
              height: "100%",
              display: "flex",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Filename overlay - adjust width to stay within the preview box */}
            {(item?.fileName || item?.original_file?.filename) && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  color: "white",
                  padding: "8px 12px",
                  zIndex: 2,
                  borderBottomLeftRadius: 1,
                  borderBottomRightRadius: 1,
                  maxWidth: "100%",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.9rem",
                    fontWeight: 700,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  Filename: {item?.fileName || item?.original_file?.filename}
                </Typography>
              </Box>
            )}
            
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: 1,
                overflow: "hidden",
                bgcolor: "rgba(0, 0, 0, 0.2)",
                position: "relative",
                zIndex: 1,
                width: "100%",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  "& img, & .react-pdf__Document": {
                    maxWidth: "100%",
                    maxHeight: "100%",
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                  },
                  "& img": {
                    margin: "auto",
                    display: "block",
                    objectPosition: "center",
                  },
                  "& .react-pdf__Page": {
                    maxWidth: "100%",
                    height: "auto !important",
                  },
                  "& audio, & .react-player": {
                    width: "100% !important",
                    maxWidth: "400px !important",
                    minHeight: "50px !important",
                    marginTop: "auto",
                    marginBottom: "auto"
                  }
                }}
              >
                {previewMediaDisplay}
              </Box>
            </Box>
          </Box>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
          />

          {/* Middle Column - Details */}
          <Box
            sx={{
              flex: isMapVisible ? "1 1 33%" : "1 1 50%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              height: "100%",
              position: "relative",
              bgcolor: "background.paper",
              borderRadius: 0,
              padding: "5px",
            }}
          >
            <Box sx={{ position: "absolute", top: 8, right: 8 }}>
              <CMChip label={item?.type} colorScheme="primary" />
            </Box>

            <Box
              sx={{
                width: "100%",
                maxWidth: "500px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                flex: 1,
                px: 3,
                height: "100%",
              }}
            >
              <Stack spacing={4} sx={{ width: "100%", alignItems: "center", flex: 1, height: "100%" }}>
                {/* Title and Description - reduced spacing */}
                <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
                  <TextField
                    value={item?.name}
                    onChange={(e) => onUpdate({ id: item?.id, name: e.target.value })}
                    variant="standard"
                    placeholder="Name"
                    InputProps={{
                      sx: {
                        fontSize: "1rem",
                        fontWeight: 500,
                        textAlign: "center",
                        "&::before": { display: "none" },
                        "&::after": { display: "none" },
                      },
                    }}
                    fullWidth
                  />
                  <TextField
                    value={item?.description || ""}
                    onChange={(e) => onUpdate({ id: item?.id, description: e.target.value })}
                    multiline
                    minRows={1}
                    maxRows={3}
                    placeholder="Description"
                    variant="standard"
                    InputProps={{
                      sx: {
                        fontSize: "0.85rem",
                        textAlign: "center",
                        "&::before": { display: "none" },
                        "&::after": { display: "none" },
                      },
                    }}
                    fullWidth
                  />
                </Stack>

                {/* Relationships Summary Section - fill available space */}
                <Stack spacing={0} sx={{ width: "100%", alignItems: "center", mt: 1, mb: 0, flex: 1, overflow: "hidden" }}>
                  <Box sx={{ 
                    width: "100%", 
                    flex: 1,
                    overflow: "auto",
                    px: 1,
                    "&::-webkit-scrollbar": {
                      width: "4px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "rgba(0,0,0,0.05)",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "rgba(0,0,0,0.15)",
                      borderRadius: "2px",
                    }
                  }}>
                    {renderRelationships()}
                  </Box>
                </Stack>

                {/* Metadata sections - reduced spacing */}
                <Stack spacing={2} sx={{ width: "100%", alignItems: "center" }}>
                  {/* No longer needed as we've moved it above */}
                </Stack>
              </Stack>
            </Box>
          </Box>

          {/* Right Column - Map */}
          {isMapVisible && (
            <Box
              sx={{
                flex: "1 1 33%",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <PreviewMap
                points={mapData.points}
                areas={mapData.areas}
                autoZoom={true}
                mapStyle="satellite"
              />
            </Box>
          )}
        </Box>
      </Paper>
      {isPreviewOpen && (
        <MediaViewer
          key={`dialog-${item?.id}-${mediaUrl || item?.previewUrl || item?.thumbnail || item?.url}`}
          item={item}
          url={mediaUrl || item?.previewUrl || item?.thumbnail || item?.url || ""}
          file={!mediaUrl && !item?.previewUrl && !item?.thumbnail && !item?.url ? item?.file : undefined}
          open={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          mode="dialog"
        />
      )}
    </>
  );
};

export default React.memo(SingleItemPreview);
